/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, type ReactNode, useMemo } from 'react';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import MoreActionsIcon from '@atlaskit/icon/core/show-more-horizontal';
import { type TriggerProps } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';

const styles = cssMap({
	base: {
		transition: 'opacity 0.1s ease-out',
	},
	hidden: {
		maxWidth: '0px',
		opacity: 0,
		overflow: 'hidden',
	},
	visible: {
		maxWidth: '32px',
		overflow: 'visible',
		opacity: 1,
	},
	animateSlideIn: {
		transition: 'max-width 0.1s ease-out, opacity 0.1s ease-out',
	},
});

interface MoreActionsTriggerProps {
	popupTriggerProps: TriggerProps;
	showMoreActionsButton: boolean;
	onToggleMoreActions: () => void;
	interactionName?: string;
	analyticsContext?: Record<string, any>;
	isMoreActionsOpened: boolean;
	moreActionsLabel: ReactNode;
	onFocus: () => void;
	onBlur: () => void;
	isSortingActive: boolean;
}

export const MoreActionsTrigger = ({
	popupTriggerProps,
	showMoreActionsButton,
	onToggleMoreActions,
	interactionName,
	analyticsContext,
	isMoreActionsOpened,
	moreActionsLabel,
	onFocus,
	onBlur,
	isSortingActive,
}: MoreActionsTriggerProps): React.JSX.Element => {
	const triggerContent = useMemo(
		() => (
			<Box
				xcss={cx(
					styles.base,
					showMoreActionsButton ? styles.visible : styles.hidden,
					isSortingActive && styles.animateSlideIn,
				)}
			>
				<IconButton
					{...popupTriggerProps}
					spacing="compact"
					appearance="subtle"
					onClick={onToggleMoreActions}
					interactionName={interactionName}
					analyticsContext={{
						componentName: 'charlieTableMoreActionsColumnButton',
						...analyticsContext,
					}}
					isSelected={isMoreActionsOpened}
					isTooltipDisabled={false}
					label={moreActionsLabel}
					onFocus={onFocus}
					onBlur={onBlur}
					icon={(iconProps) => (
						<Fragment>
							{showMoreActionsButton && (
								<MoreActionsIcon {...iconProps} color="currentColor" size="small" />
							)}
						</Fragment>
					)}
				/>
			</Box>
		),
		[
			showMoreActionsButton,
			isSortingActive,
			popupTriggerProps,
			onToggleMoreActions,
			interactionName,
			analyticsContext,
			isMoreActionsOpened,
			moreActionsLabel,
			onFocus,
			onBlur,
		],
	);

	return triggerContent;
};
